/// <reference types="google.maps" />
import { useEffect, useRef, useState } from "react";
import { MapPin, Search } from "lucide-react";

export type MapPoint = { latitude: number; longitude: number; address: string };
type Boundary = { centerLatitude: number | null; centerLongitude: number | null; radiusMiles: number };
let mapsPromise: Promise<void> | undefined;
let loadedKey = "";
const mapsFailures = new Set<(message: string) => void>();
function loadMaps(key: string) {
  if (loadedKey && loadedKey !== key) return Promise.reject(new Error("The Maps key has changed. Refresh this page to use the new key."));
  if (!mapsPromise) {
    loadedKey = key;
    mapsPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&libraries=marker&v=weekly&callback=digitalCafeMapsReady`;
      const callbacks = window as unknown as Record<string, unknown>;
      callbacks.digitalCafeMapsReady = () => { delete callbacks.digitalCafeMapsReady; resolve(); };
      script.onerror = () => { delete callbacks.digitalCafeMapsReady; script.remove(); mapsPromise = undefined; loadedKey = ""; reject(new Error("Google Maps could not load. Check your connection and try again.")); };
      callbacks.gm_authFailure = () => {
        const message = "Google Maps authorization failed. Please check the Maps API key in Admin Settings.";
        mapsFailures.forEach(report => report(message));
        reject(new Error(message));
      };
      document.head.appendChild(script);
    });
  }
  return mapsPromise;
}

export function DeliveryMap({ apiKey, point, onChange, boundary, locateOnOpen = false, label, readOnly = false }: {
  apiKey: string; point: MapPoint | null; onChange: (point: MapPoint) => void; boundary?: Boundary; locateOnOpen?: boolean; label: string; readOnly?: boolean;
}) {
  const host = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map>();
  const marker = useRef<google.maps.marker.AdvancedMarkerElement>();
  const circle = useRef<google.maps.Circle>();
  const latest = useRef({ point, onChange });
  latest.current = { point, onChange };
  const generation = useRef(0);
  const selection = useRef<{ lat: number; lng: number } | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<google.maps.GeocoderResult[]>([]);
  const [searching, setSearching] = useState(false);
  const mounted = useRef(false);

  async function pin(location: google.maps.LatLngLiteral) {
    const request = ++generation.current;
    selection.current = location;
    // Commit the selected coordinates immediately so an old pin cannot be submitted during lookup.
    latest.current.onChange({ latitude: location.lat, longitude: location.lng, address: `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}` });
    setError(""); setNotice(""); setResults([]);
    try {
      const response = await new google.maps.Geocoder().geocode({ location });
      if (mounted.current && request === generation.current) {
        latest.current.onChange({ latitude: location.lat, longitude: location.lng, address: response.results[0]?.formatted_address || `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}` });
      }
    } catch {
      if (mounted.current && request === generation.current) setNotice("Address lookup is unavailable. Your pin is selected; add delivery details below.");
    }
  }

  function locate() {
    if (!navigator.geolocation) { setNotice("Location is unavailable. Search for your address or click the map."); return; }
    const request = ++generation.current;
    setNotice("Finding your current location…");
    navigator.geolocation.getCurrentPosition(position => {
      if (!mounted.current || request !== generation.current) return;
      setNotice("");
      void pin({ lat: position.coords.latitude, lng: position.coords.longitude });
    }, () => {
      if (mounted.current && request === generation.current) setNotice("Could not access your location. Allow location access, search for your address, or click the map.");
    }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
  }

  useEffect(() => {
    mounted.current = true;
    let disposed = false;
    const reportFailure = (message: string) => { setError(message); setReady(false); };
    mapsFailures.add(reportFailure);
    setReady(false); setError("");
    if (apiKey.trim()) void loadMaps(apiKey.trim()).then(async () => {
      if (disposed || !host.current) return;
      const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
      if (disposed) return;
      const p = latest.current.point;
      map.current = new google.maps.Map(host.current!, { center: p ? { lat: p.latitude, lng: p.longitude } : { lat: 16.8409, lng: 96.1735 }, zoom: 13, mapId: "DEMO_MAP_ID", streetViewControl: false, mapTypeControl: false, fullscreenControl: true });
      marker.current = new AdvancedMarkerElement({ map: map.current, gmpDraggable: !readOnly, title: label });
      circle.current = new google.maps.Circle({ map: map.current, clickable: false, strokeColor: "#f45c35", strokeOpacity: 0.85, fillColor: "#f45c35", fillOpacity: 0.12 });
      if (!readOnly) {
        map.current.addListener("click", (event: google.maps.MapMouseEvent) => { if (event.latLng) void pin(event.latLng.toJSON()); });
        marker.current.addListener("dragend", (event: google.maps.MapMouseEvent) => { if (event.latLng) void pin(event.latLng.toJSON()); });
      }
      setReady(true);
      if (!readOnly && locateOnOpen && !p) locate();
    }).catch(failure => { if (!disposed) setError(failure instanceof Error ? failure.message : "Unable to load Google Maps."); });
    return () => {
      disposed = true; mounted.current = false; generation.current++;
      mapsFailures.delete(reportFailure);
      if (map.current) google.maps.event.clearInstanceListeners(map.current);
      if (marker.current) { google.maps.event.clearInstanceListeners(marker.current); marker.current.map = null; }
      circle.current?.setMap(null);
      map.current = undefined; marker.current = undefined; circle.current = undefined;
    };
  }, [apiKey, readOnly]);

  useEffect(() => {
    if (!ready || !map.current || !circle.current) return;
    if (boundary?.centerLatitude != null && boundary.centerLongitude != null && Number.isFinite(boundary.radiusMiles) && boundary.radiusMiles > 0) {
      circle.current.setOptions({ center: { lat: boundary.centerLatitude, lng: boundary.centerLongitude }, radius: boundary.radiusMiles * 1609.344, visible: true });
      const bounds = circle.current.getBounds();
      if (bounds) { if (point) bounds.extend({ lat: point.latitude, lng: point.longitude }); map.current.fitBounds(bounds); }
    } else circle.current.setVisible(false);
  }, [ready, boundary?.centerLatitude, boundary?.centerLongitude, boundary?.radiusMiles]);

  useEffect(() => {
    if ((point && (!selection.current || point.latitude !== selection.current.lat || point.longitude !== selection.current.lng)) || (!point && selection.current)) {
      generation.current++;
      selection.current = point ? { lat: point.latitude, lng: point.longitude } : null;
      setNotice("");
      setResults([]);
    }
    if (!ready || !marker.current || !map.current) return;
    marker.current.position = point ? { lat: point.latitude, lng: point.longitude } : null;
    if (point) map.current.panTo({ lat: point.latitude, lng: point.longitude });
  }, [ready, point?.latitude, point?.longitude]);

  async function search() {
    if (!query.trim() || !ready) return;
    const request = ++generation.current;
    setSearching(true); setError(""); setResults([]);
    try {
      const response = await new google.maps.Geocoder().geocode({ address: query.trim(), bounds: map.current?.getBounds() });
      if (mounted.current && request === generation.current) setResults(response.results);
    } catch { if (mounted.current && request === generation.current) setError("No address found or address search is unavailable. Try a fuller address or click the map."); }
    finally { if (mounted.current) setSearching(false); }
  }

  return <div className="delivery-map-picker">
    <strong>{label}</strong>
    {!apiKey.trim() ? <div className="status-banner error">Google Maps is not configured. Add the Maps API key in Admin Settings.</div> : <>
      {!readOnly ? <div className="map-search"><input aria-label={`${label} search`} placeholder="Search an address" value={query} onChange={event => setQuery(event.target.value)} onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); void search(); } }} /><button className="secondary-button" type="button" disabled={!ready || searching || !query.trim()} onClick={() => void search()}><Search size={17} />{searching ? "Searching…" : "Search"}</button></div> : null}
      {!readOnly && results.length ? <div className="map-search-results">{results.map(result => <button type="button" key={result.place_id} onClick={() => { generation.current++; setNotice(""); setResults([]); selection.current = { lat: result.geometry.location.lat(), lng: result.geometry.location.lng() }; latest.current.onChange({ latitude: result.geometry.location.lat(), longitude: result.geometry.location.lng(), address: result.formatted_address }); }}><MapPin size={16} />{result.formatted_address}</button>)}</div> : null}
      <div className="delivery-map" ref={host} aria-label={label} />
      {!ready && !error ? <small>Loading Google Maps…</small> : null}
      {!readOnly ? <button className="secondary-button" type="button" disabled={!ready} onClick={locate}><MapPin size={16} />Use my current location</button> : null}
      {!readOnly ? <small>Search, click the map, or drag the pin to choose an address.</small> : null}
    </>}
    {point ? <div className="map-selected-address"><MapPin size={16} /><span>{point.address}<small>{point.latitude.toFixed(6)}, {point.longitude.toFixed(6)}</small></span></div> : null}
    {notice ? <div className="status-banner" role="status">{notice}</div> : null}
    {error ? <div className="status-banner error" role="alert">{error}</div> : null}
  </div>;
}
